import { Suspense } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  Flex,
  Heading,
  Icon,
  Image,
  InlineStack,
  Link,
  Page,
  PageBody,
  PageLoader,
  Paragraph,
  Stack,
  Tooltip,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useSqlImports } from '@/services/db/sql-import/queries';
import { useObjectTypes } from '@/services/db/object-type/queries';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { SQL_DATABASE_CATALOG } from '@/features/sql-sync';
import { SQL_SYNC_NEW_ROUTE_PATHS } from './hooks';
import { SqlImportListItem } from './components/sql-import-list-item';
import { groupSqlImportsBySyncTarget } from './utils';

const CREATE_PERMISSION = 'journeys/sql_syncs/create';

const CREATE_TOOLTIP =
  'Managing syncs requires Workspace manager permissions or greater. Contact your workspace admin to increase your permissions.';

type CreateSyncButtonProps = {
  href: string;
  children: string;
  'data-test-create-sql-import-sync-button'?: boolean;
  'data-test-create-sync'?: string;
};

/** The wizard entry, disabled with an explanation for viewers who cannot create syncs. */
function CreateSyncButton({ href, children, ...rest }: CreateSyncButtonProps) {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(CREATE_PERMISSION);

  return (
    <Tooltip isDisabled={canCreate} content={CREATE_TOOLTIP} isCustom={true}>
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="primary"
          href={href}
          isDisabled={!canCreate}
          {...rest}
        >
          {children}
        </Button>
      )}
    </Tooltip>
  );
}

type SqlSyncListPageProps = {
  workspaceId: string;
  type: SqlDatabaseType;
};

/**
 * The per-database SQL sync list: every import reading from one engine, grouped
 * by what it writes to (people, then each object type), or the blank slate that
 * invites the first one. The create wizard and the import detail it links to are
 * still served by the embedded Ember app.
 */
export default function SqlSyncListPage(props: SqlSyncListPageProps) {
  return (
    <Suspense fallback={<PageLoader label="Loading SQL sync…" />}>
      <SqlSyncList {...props} />
    </Suspense>
  );
}

function SqlSyncList({ workspaceId, type }: SqlSyncListPageProps) {
  const { data: sqlImports } = useSqlImports({ database: type, workspaceId: Number(workspaceId) });
  const { data: objectTypes } = useObjectTypes({ workspaceId: Number(workspaceId) });
  const router = useRouter();
  const info = SQL_DATABASE_CATALOG[type];
  const newSyncHref = router.buildLocation({
    to: SQL_SYNC_NEW_ROUTE_PATHS.migrated,
    params: { workspaceId, type },
  }).href;

  if (sqlImports.length === 0) {
    return (
      <Page>
        <PageBody>
          <Stack alignBlock="center" alignInline="center">
            <EmptyState>
              <EmptyStateGraphic>
                <Image src={info.imageSrc} size="icon-v2-xl" alt="" />
              </EmptyStateGraphic>
              <EmptyStateTitle>
                Sync people or custom objects from your {info.name} {info.store}
              </EmptyStateTitle>
              <EmptyStateDescription>
                Add and update people or custom objects from your {info.name} database to your workspace or manual
                segments.
              </EmptyStateDescription>
              <EmptyStateActions>
                <CreateSyncButton href={newSyncHref} data-test-create-sync={type}>
                  Set up sync
                </CreateSyncButton>
              </EmptyStateActions>
            </EmptyState>
            <Flex alignItems="center">
              <Icon name="help" size="sm" mr="100" />
              <Paragraph>
                Learn more about{' '}
                <Link
                  variant="secondary"
                  href={info.documentationUrl}
                  isExternal
                  data-test-sql-import-documentation-url
                >
                  syncing from {info.name}
                </Link>
              </Paragraph>
            </Flex>
          </Stack>
        </PageBody>
      </Page>
    );
  }

  const groups = groupSqlImportsBySyncTarget(sqlImports, objectTypes, 'People');

  return (
    <Page>
      <PageBody>
        <InlineStack alignBlock="center" alignInline="space-between">
          <InlineStack alignBlock="center" gap="100">
            <Image src={info.imageSrc} size="icon-v2-md" alt="" />
            <Heading level="1" text="product-h2" color="bold">
              {info.name}
            </Heading>
          </InlineStack>
          <CreateSyncButton href={newSyncHref} data-test-create-sql-import-sync-button>
            Add new
          </CreateSyncButton>
        </InlineStack>

        {groups.map((group) => (
          <Stack key={group.name} mt="300" gap="200">
            <Heading level="2" text="product-h3" color="bold">
              {group.name} Sync
            </Heading>
            {group.imports.map((sqlImport) => (
              <SqlImportListItem key={sqlImport.id} workspaceId={workspaceId} sqlImport={sqlImport} />
            ))}
          </Stack>
        ))}
      </PageBody>
    </Page>
  );
}
